/**
 * src/components/spatial/SpatialPreviewMap.tsx
 *
 * Inline cross-provider preview map for the Spatial Objects admin
 * panel's Preview tab. Mirrors the "Provider preview" experience
 * documented in `GEOLINK_PROVIDER_PREVIEW.md` §5: a single
 * `featureCollection + style.layers` payload from
 * GeoLink's /render endpoint is rendered identically against
 * Mapbox GL JS (for `provider=mapbox`) or MapLibre GL JS (for
 * `provider=maplibre` / `provider=maptiler` / `provider=generic`).
 *
 * Why two engines:
 *   - Mapbox GL JS v3 is closed-source and ships only with a Mapbox
 *     access token. We only use it when the operator selected
 *     `mapbox` AND a token is available.
 *   - MapLibre GL JS is the open-source fork that consumes any
 *     provider's vector tiles, including MapTiler (whose SDK is built
 *     on MapLibre). Every provider mode whose basemap doesn't require
 *     a Mapbox token routes through MapLibre.
 *
 * The preview deliberately does NOT touch the host app's main map —
 * it owns its own DOM container and engine instance, lives only while
 * the Preview tab is open, and tears down on unmount.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type {
  SpatialObjectsRenderEnvelope,
  SpatialRenderProvider,
} from '../../services/spatial/geoLinkTypes';
import { getMapboxToken, getMapTilerKey } from '../../services/spatial/spatialEnv';

// Stable source id used both for the GeoJSON source and the layer
// `source` property — must match what GeoLink ships in style.sourceId,
// which is "geolink-spatial-objects" by convention. We keep it local
// here so the preview stays independent of the main map's source id.
const PREVIEW_SOURCE_ID = 'gl-spatial-preview';

type EngineKind = 'mapbox' | 'maplibre';

interface EngineChoice {
  engine: EngineKind;
  style:  string | maplibregl.StyleSpecification;
  /**
   * Why we picked this style — surfaced in the UI so the user knows
   * whether they're looking at a real basemap or the offline-safe
   * dark fallback (and what env var would unlock the basemap).
   */
  reason: 'mapbox-token' | 'maptiler-key' | 'no-key';
}

/**
 * Inline minimal MapLibre style — a single dark "background" layer
 * with no external sources. Used as the offline-safe fallback when a
 * provider's tile host isn't reachable (e.g. CSP blocks
 * demotiles.maplibre.org or the MapTiler key is missing). The spatial
 * objects still render on top because they live in a separate
 * GeoJSON source we add at runtime.
 */
const INLINE_FALLBACK_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    {
      id: 'gl-spatial-preview-bg',
      type: 'background',
      paint: { 'background-color': '#0b1220' },
    },
  ],
};

/**
 * Decide which engine + base style to use for a given preview provider.
 * Hierarchy:
 *   - provider=mapbox + Mapbox token  → Mapbox GL JS, light-v11
 *   - provider=maptiler + MapTiler key → MapLibre + MapTiler streets-v2
 *   - everything else                  → MapLibre + INLINE_FALLBACK_STYLE
 *
 * The inline fallback makes the preview robust against network and
 * Content-Security-Policy restrictions: no external fetch is required
 * to draw the basemap layer, only the spatial-object overlays.
 */
function decideEngine(provider: SpatialRenderProvider): EngineChoice {
  const mapboxToken   = getMapboxToken();
  const maptilerKey   = getMapTilerKey();
  if (provider === 'mapbox' && mapboxToken) {
    return {
      engine: 'mapbox',
      style:  'mapbox://styles/mapbox/light-v11',
      reason: 'mapbox-token',
    };
  }
  // For maplibre / maptiler / generic — any provider that doesn't
  // require a Mapbox token — prefer MapTiler tiles when a key is
  // configured. The streets-v2 style ships a `building` source layer
  // with `render_height` / `render_min_height` properties so we can
  // mount a 3D extrusion on top for visual parity with the main map.
  if (maptilerKey) {
    return {
      engine: 'maplibre',
      style:  `https://api.maptiler.com/maps/streets-v2/style.json?key=${encodeURIComponent(maptilerKey)}`,
      reason: 'maptiler-key',
    };
  }
  return {
    engine: 'maplibre',
    style:  INLINE_FALLBACK_STYLE,
    reason: 'no-key',
  };
}

/**
 * Add a Mapbox-style 3D-building extrusion layer to the active engine.
 *
 * Source detection: the source name varies between engines AND between
 * MapTiler styles (streets-v2 used to expose `openmaptiles`, newer
 * versions expose just one source whose name changes per style). We
 * introspect `map.getStyle().sources` and find a vector source that
 * actually has a `building` source-layer — and only mount the
 * extrusion when one exists. Best-effort: silently no-ops when the
 * style doesn't expose a building source-layer (e.g. the offline
 * fallback).
 */
function add3DBuildingsLayer(
  map: mapboxgl.Map | maplibregl.Map,
  engine: EngineKind,
  beforeId?: string,
): void {
  const layerId = 'gl-preview-3d-buildings';
  try {
    if ((map as { getLayer?: (id: string) => unknown }).getLayer?.(layerId)) return;
    if (engine === 'mapbox') {
      // Mapbox standard styles always ship a `composite` source with a
      // `building` source-layer.
      const sources = (map as mapboxgl.Map).getStyle?.()?.sources ?? {};
      if (!sources.composite) return;
      (map as mapboxgl.Map).addLayer(
        {
          id: layerId,
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          paint: {
            'fill-extrusion-color':   '#aab0c0',
            'fill-extrusion-height':  ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
            'fill-extrusion-base':    ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
            'fill-extrusion-opacity': 0.65,
          },
        } as unknown as mapboxgl.AnyLayer,
        beforeId,
      );
      return;
    }
    // MapLibre / MapTiler — the source name depends on the style.
    // Inspect every layer the style already shipped and reuse the
    // source/source-layer pair of any existing `building` layer. That
    // works whether the style calls its source `openmaptiles`,
    // `maptiler_planet`, or anything else.
    const styleObj = (map as maplibregl.Map).getStyle?.();
    const layers   = styleObj?.layers ?? [];
    type LL = { id?: string; source?: string; 'source-layer'?: string; type?: string };
    const buildingLayer = (layers as LL[]).find(l =>
      typeof l['source-layer'] === 'string' && l['source-layer'] === 'building',
    );
    if (!buildingLayer || !buildingLayer.source) {
      // Style doesn't expose a building source-layer — skip silently.
      return;
    }
    (map as maplibregl.Map).addLayer(
      {
        id: layerId,
        source: buildingLayer.source,
        'source-layer': 'building',
        type: 'fill-extrusion',
        minzoom: 14,
        paint: {
          'fill-extrusion-color':   '#aab0c0',
          'fill-extrusion-height':  ['coalesce', ['get', 'render_height'], ['get', 'height'], 0],
          'fill-extrusion-base':    ['coalesce', ['get', 'render_min_height'], ['get', 'min_height'], 0],
          'fill-extrusion-opacity': 0.65,
        },
      } as unknown as maplibregl.LayerSpecification,
      beforeId,
    );
  } catch {
    // Style doesn't expose a building source-layer (e.g. fallback) —
    // silently skip so the preview stays usable.
  }
}

/**
 * Add GeoTrust's own default spatial-object layers (extrusion / point /
 * line) for the preview source. This is a fallback for when GeoLink's
 * `/render` envelope ships an EMPTY `style.layers` array (or layers the
 * engine couldn't install) — without it the Preview tab shows a basemap
 * with no buildings even though the FeatureCollection has features.
 *
 * Paint expressions read the canonical property names first, then the
 * provider aliases, so the same code works across mapbox/maplibre/
 * maptiler/generic envelopes. Returns the ids it actually added.
 */
function addDefaultSpatialLayers(map: mapboxgl.Map | maplibregl.Map, sourceId: string): string[] {
  const added: string[] = [];
  const tryAdd = (spec: Record<string, unknown>) => {
    const id = spec.id as string;
    try {
      if ((map as { getLayer?: (i: string) => unknown }).getLayer?.(id)) return;
      (map as { addLayer: (s: unknown) => void }).addLayer({ ...spec, source: sourceId });
      added.push(id);
    } catch { /* engine rejected the spec — skip */ }
  };

  const colorExpr = ['coalesce', ['get', 'colorKey'], ['get', 'fill_color'], '#4A90E2'];
  const heightExpr = ['coalesce', ['get', 'heightMeters'], ['get', 'height'], ['get', 'render_height'], 30];
  const baseExpr = ['coalesce', ['get', 'baseHeightMeters'], ['get', 'base_height'], ['get', 'render_min_height'], 0];

  // Polygon footprints → 3D extrusion (buildings / floors / zones / slices).
  tryAdd({
    id: 'gl-preview-spatial-extrusion',
    type: 'fill-extrusion',
    filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
    paint: {
      'fill-extrusion-color': colorExpr,
      'fill-extrusion-height': heightExpr,
      'fill-extrusion-base': baseExpr,
      'fill-extrusion-opacity': 0.85,
    },
  });
  // Line geometries.
  tryAdd({
    id: 'gl-preview-spatial-line',
    type: 'line',
    filter: ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false],
    paint: { 'line-color': colorExpr, 'line-width': 3 },
  });
  // Point geometries → circle markers.
  tryAdd({
    id: 'gl-preview-spatial-point',
    type: 'circle',
    filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
    paint: {
      'circle-radius': 6,
      'circle-color': colorExpr,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  });

  return added;
}

/**
 * Walk a GeoJSON `coordinates` payload (any nesting depth) and call
 * `visit` for every leaf [lng, lat] pair. Used to build a fitBounds
 * box without depending on turf in this lightweight preview.
 */
function visitCoords(coords: unknown, visit: (lng: number, lat: number) => void): void {
  if (!Array.isArray(coords)) return;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    visit(coords[0] as number, coords[1] as number);
    return;
  }
  for (const c of coords) visitCoords(c, visit);
}

export interface SpatialPreviewMapProps {
  /** Active provider — drives both the basemap engine and the data
   *  request's `?provider=` query string. */
  provider: SpatialRenderProvider;
  /** Render envelope returned by GeoLink (`/render` or
   *  `/admin/.../preview`). Mounted as the GeoJSON source + suggested
   *  style layers inside this preview map. */
  envelope: SpatialObjectsRenderEnvelope | null;
  /** Pixel height of the map container. */
  height?: number | string;
}

const SpatialPreviewMap: React.FC<SpatialPreviewMapProps> = ({
  provider,
  envelope,
  height = 360,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef       = useRef<mapboxgl.Map | maplibregl.Map | null>(null);
  const engineRef    = useRef<EngineKind | null>(null);
  // Track applied envelope so we don't reapply the same data twice
  // (e.g. on re-render with the same prop reference).
  const appliedRef   = useRef<SpatialObjectsRenderEnvelope | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [reason, setReason] = useState<EngineChoice['reason']>('no-key');
  // Count of style.layers that failed to install (e.g. expression not
  // supported by the active engine). Surfaced under the map so the user
  // knows when the dark-only preview is genuinely empty by design vs.
  // hiding silent layer-install errors.
  const [skippedLayers, setSkippedLayers] = useState<string[]>([]);

  /** Build (or rebuild) the map for the given provider. We reuse the
   *  existing instance when possible — calling setStyle is much faster
   *  than a full remove/recreate. */
  const buildMap = useCallback(() => {
    if (!containerRef.current) return;
    const choice = decideEngine(provider);
    const { engine, style } = choice;
    setReason(choice.reason);

    if (mapRef.current && engineRef.current === engine) {
      try {
        (mapRef.current as { setStyle: (s: typeof style) => void }).setStyle(style);
        appliedRef.current = null; // style change wipes sources, force reapply
      } catch (err) {
      }
      return;
    }

    if (mapRef.current) {
      try { mapRef.current.remove(); } catch { /* ignore */ }
      mapRef.current = null;
    }

    if (engine === 'mapbox') {
      const token = getMapboxToken();
      if (token) mapboxgl.accessToken = token;
      const m = new mapboxgl.Map({
        container: containerRef.current,
        style: style as string,
        center: [0, 20],
        zoom:   1.2,
        pitch:  45,
        bearing: 0,
      });
      m.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
      m.on('error', (e) => {
        const msg = (e?.error as { message?: string } | undefined)?.message || 'Map error';
        setError(msg);
      });
      m.on('load', () => add3DBuildingsLayer(m, 'mapbox'));
      mapRef.current = m;
    } else {
      const m = new maplibregl.Map({
        container: containerRef.current,
        style,
        center: [0, 20],
        zoom:   1.2,
        pitch:  45,
        bearing: 0,
      });
      m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      m.on('error', (e) => {
        const msg = (e?.error as { message?: string } | undefined)?.message || 'Map error';
        setError(msg);
      });
      m.on('load', () => add3DBuildingsLayer(m, 'maplibre'));
      mapRef.current = m;
    }
    engineRef.current  = engine;
    appliedRef.current = null;
  }, [provider]);

  // (Re)build when the provider changes; tear down on unmount.
  useEffect(() => {
    buildMap();
    return () => {
      try { mapRef.current?.remove(); } catch { /* ignore */ }
      mapRef.current    = null;
      engineRef.current = null;
      appliedRef.current = null;
    };
  }, [buildMap]);

  // Apply the envelope (data + suggested style.layers) once the basemap
  // style is loaded. Re-runs when either the envelope or the active
  // engine changes.
  useEffect(() => {
    setError(null);
    setSkippedLayers([]);
    const map = mapRef.current;
    if (!map || !envelope) return;
    if (appliedRef.current === envelope) return; // already applied

    const apply = () => {
      try {
        // 1) GeoJSON source.
        const fc = envelope.featureCollection;
        const existing = (map as { getSource?: (id: string) => unknown }).getSource?.(PREVIEW_SOURCE_ID) as
          | { setData?: (d: unknown) => void }
          | undefined;
        if (existing && typeof existing.setData === 'function') {
          existing.setData(fc);
        } else {
          (map as { addSource: (id: string, src: unknown) => void }).addSource(PREVIEW_SOURCE_ID, {
            type: 'geojson',
            data: fc,
          });
        }

        // 2) Style layers from the envelope. Skip ones that are already
        // installed (style retains them across setData calls).
        const layers  = envelope.style?.layers ?? [];
        const skipped: string[] = [];
        let renderableInstalled = 0;
        layers.forEach((layer) => {
          const layerId = layer.id;
          const has = (map as { getLayer?: (id: string) => unknown }).getLayer?.(layerId);
          if (has) {
            if (layer.type === 'fill-extrusion' || layer.type === 'fill' || layer.type === 'circle' || layer.type === 'line') {
              renderableInstalled += 1;
            }
            return;
          }
          try {
            (map as { addLayer: (spec: unknown) => void }).addLayer({
              ...layer,
              source: PREVIEW_SOURCE_ID,
            });
            if (layer.type === 'fill-extrusion' || layer.type === 'fill' || layer.type === 'circle' || layer.type === 'line') {
              renderableInstalled += 1;
            }
          } catch (err) {
            skipped.push(layer.id);
          }
        });

        // Fallback: when GeoLink's envelope shipped no usable spatial
        // layers (empty style.layers, or all of them failed to install),
        // mount GeoTrust's own default extrusion/line/point layers so the
        // objects actually render instead of an empty basemap.
        if (renderableInstalled === 0) {
          addDefaultSpatialLayers(map, PREVIEW_SOURCE_ID);
        }
        setSkippedLayers(skipped);

        // 3) Click popups on the extrusion + circle layers (the two
        // operators usually want to click). Use the suggested layer
        // ids from the envelope; fall back to the conventional names
        // shipped by GeoLink today.
        const interactiveIds = (layers
          .filter((l) => l.type === 'fill-extrusion' || l.type === 'circle')
          .map((l) => l.id))
          .concat([
            'geolink-spatial-objects-extrusion',
            'geolink-spatial-objects-point',
            'gl-preview-spatial-extrusion',
            'gl-preview-spatial-point',
          ]);
        const seenIds = new Set<string>();
        interactiveIds.forEach((layerId) => {
          if (seenIds.has(layerId)) return;
          seenIds.add(layerId);
          if (!(map as { getLayer?: (id: string) => unknown }).getLayer?.(layerId)) return;
          (map as {
            on: (
              event: 'click' | 'mouseenter' | 'mouseleave',
              layerOrHandler: string | (() => void),
              handler?: (e: { lngLat: { lng: number; lat: number }; features?: Array<{ properties?: Record<string, unknown> }> }) => void,
            ) => void;
          }).on('click', layerId, (e) => {
            const f = e.features?.[0];
            if (!f) return;
            const p = f.properties ?? {};
            const name      = String(p.name ?? p.objectId ?? 'Spatial object');
            const objectType = String(p.objectType ?? 'object');
            const heightM   = p.heightMeters ?? p.height ?? '';
            const ruleId    = p.executionRuleId;
            const html =
              `<div style="font-family:system-ui;font-size:12px;line-height:1.45;max-width:240px;">` +
              `<div style="font-weight:600;margin-bottom:2px;">${escapeHtml(name)}</div>` +
              `<div style="color:#6b7280">${escapeHtml(objectType)}` +
              (heightM !== '' ? ` &middot; ${escapeHtml(String(heightM))} m` : '') +
              `</div>` +
              (ruleId != null
                ? `<div style="margin-top:4px;color:#0ea5e9">Linked rule #${escapeHtml(String(ruleId))}</div>`
                : '') +
              `</div>`;
            // Branch on the engine instead of sharing the constructor:
            // `mapboxgl.Popup | maplibregl.Popup` is a union, and `.addTo()`
            // on that union requires `mapboxgl.Map & maplibregl.Map`, which
            // doesn't compile.
            const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
            if (engineRef.current === 'mapbox') {
              new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
                .setLngLat(lngLat)
                .setHTML(html)
                .addTo(map as mapboxgl.Map);
            } else {
              new maplibregl.Popup({ closeButton: true, closeOnClick: true })
                .setLngLat(lngLat)
                .setHTML(html)
                .addTo(map as maplibregl.Map);
            }
          });
          (map as { on: (e: 'mouseenter' | 'mouseleave', l: string, h: () => void) => void }).on('mouseenter', layerId, () => {
            try { (map as { getCanvas: () => HTMLCanvasElement }).getCanvas().style.cursor = 'pointer'; } catch { /* ignore */ }
          });
          (map as { on: (e: 'mouseenter' | 'mouseleave', l: string, h: () => void) => void }).on('mouseleave', layerId, () => {
            try { (map as { getCanvas: () => HTMLCanvasElement }).getCanvas().style.cursor = ''; } catch { /* ignore */ }
          });
        });

        // 4) Frame the data. We compute a bounds box manually so we
        // don't pull in turf for this preview-only path.
        let hasBounds = false;
        let west  =  Infinity;
        let south =  Infinity;
        let east  = -Infinity;
        let north = -Infinity;
        fc.features?.forEach((f) => {
          const g = f.geometry as { coordinates?: unknown } | undefined;
          if (!g) return;
          visitCoords(g.coordinates, (lng, lat) => {
            hasBounds = true;
            if (lng < west)  west  = lng;
            if (lng > east)  east  = lng;
            if (lat < south) south = lat;
            if (lat > north) north = lat;
          });
        });
        if (hasBounds) {
          // The "Object id (optional)" field controls whether the
          // /render envelope ships a single object or the full
          // collection. Full-collection envelopes can span the globe
          // (continent-scale bbox), and `fitBounds` happily zooms out
          // to fit them — which makes individual objects too small to
          // see. Detect that case and instead snap the camera to the
          // FIRST feature's bounds so the user always lands on a
          // recognisable object. Threshold of 8° catches anything
          // bigger than a country-sized region.
          const widthDeg  = east  - west;
          const heightDeg = north - south;
          const TOO_WIDE  = 8; // degrees
          if (widthDeg > TOO_WIDE || heightDeg > TOO_WIDE) {
            // Find the bounds of just the first feature in the FC and
            // fit to that. Operators can still click another feature's
            // popup to see it.
            const first = fc.features?.[0];
            const g = first?.geometry as { coordinates?: unknown } | undefined;
            let fw = Infinity, fs = Infinity, fe = -Infinity, fn = -Infinity;
            if (g) {
              visitCoords(g.coordinates, (lng, lat) => {
                if (lng < fw) fw = lng;
                if (lng > fe) fe = lng;
                if (lat < fs) fs = lat;
                if (lat > fn) fn = lat;
              });
            }
            if (Number.isFinite(fw)) {
              (map as { fitBounds: (b: [[number, number], [number, number]], opts?: unknown) => void }).fitBounds(
                [[fw, fs], [fe, fn]],
                { padding: 80, maxZoom: 18, duration: 400, pitch: 45 },
              );
            } else {
              // Couldn't get even the first feature — fall back to the
              // full extent so SOMETHING is visible.
              (map as { fitBounds: (b: [[number, number], [number, number]], opts?: unknown) => void }).fitBounds(
                [[west, south], [east, north]],
                { padding: 80, maxZoom: 18, duration: 400, pitch: 45 },
              );
            }
          } else {
            // Tight padding + short duration so the preview snaps to
            // the object the moment data arrives. maxZoom=18 lets
            // large skyscrapers fill the frame the same way the main
            // map does when you double-click an object marker.
            (map as { fitBounds: (b: [[number, number], [number, number]], opts?: unknown) => void }).fitBounds(
              [[west, south], [east, north]],
              { padding: 80, maxZoom: 18, duration: 400, pitch: 45 },
            );
          }
        }

        appliedRef.current = envelope;
      } catch (err) {
        setError((err as Error).message);
      }
    };

    const isLoaded = (map as { isStyleLoaded?: () => boolean }).isStyleLoaded?.();
    if (isLoaded) {
      apply();
    } else {
      (map as { once: (e: 'load', cb: () => void) => void }).once('load', apply);
    }
  }, [envelope]);

  // Human-readable hint about why the basemap looks the way it does.
  // Nothing actually breaks with `reason='no-key'` — the spatial overlay
  // still renders on top of a dark background — but the user shouldn't
  // mistake the dark square for a broken preview.
  const basemapHint = (() => {
    if (reason === 'mapbox-token') return null;
    if (reason === 'maptiler-key') return null;
    // no-key
    if (provider === 'mapbox') {
      return 'No REACT_APP_MAPBOX_TOKEN configured — using offline-safe dark fallback. Spatial objects still render on top.';
    }
    if (provider === 'maptiler') {
      return 'No REACT_APP_MAPTILER_KEY configured — using offline-safe dark fallback. Spatial objects still render on top.';
    }
    return 'No basemap key configured for this provider — using offline-safe dark fallback. Spatial objects still render on top.';
  })();

  return (
    <div>
      {basemapHint && (
        <div style={{
          padding:      8,
          marginBottom: 8,
          background:   'rgba(14,165,233,0.10)',
          color:        '#7dd3fc',
          fontSize:     11,
          borderRadius: 4,
        }}>
          {basemapHint}
        </div>
      )}
      {error && (
        <div style={{
          padding:   8,
          marginBottom: 8,
          background:'rgba(239,68,68,0.1)',
          color:     '#fca5a5',
          fontSize:  11,
          borderRadius: 4,
        }}>
          Preview error: {error}
        </div>
      )}
      {skippedLayers.length > 0 && (
        <div style={{
          padding:      8,
          marginBottom: 8,
          background:   'rgba(234,179,8,0.10)',
          color:        '#fde68a',
          fontSize:     11,
          borderRadius: 4,
        }}>
          Skipped {skippedLayers.length} layer(s) the active engine couldn't install: {skippedLayers.join(', ')}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          width:        '100%',
          height,
          minHeight:    240,
          border:       '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          overflow:     'hidden',
          background:   '#0b1220',
        }}
      />
      <div style={{ marginTop: 6, fontSize: 10, color: '#9ca3af', lineHeight: 1.4 }}>
        Engine: <strong>{engineRef.current ?? '…'}</strong>
        {' '}· Basemap: <code>{reason === 'mapbox-token' ? 'mapbox light-v11' : reason === 'maptiler-key' ? 'maptiler streets-v2' : 'inline fallback'}</code>
        {' '}· Source: <code>{PREVIEW_SOURCE_ID}</code>
        {' '}· Layers: <code>{envelope?.style?.layers?.length ?? 0}</code>
        {' '}· Provider: <code>{provider}</code>
      </div>
    </div>
  );
};

/** Tiny HTML escaper used by the popup template. */
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default SpatialPreviewMap;
